import { Button } from "../components/Button";
import { BrandMark } from "../components/BrandMark";
import { IntroStage, shouldShowIntro } from "../components/IntroStage";
import { useAuth } from "../auth/AuthContext";
import { authStore } from "../auth/authStore";
import { useFlow } from "../flow/FlowContext";
import { matchStore } from "../matching/matchStore";
import { apiHealth } from "../api/client";
import { apiJson } from "../api/client";
import { isServerMode } from "../api/backend";
import { LanguagePicker, useT } from "../i18n";
import { VoiceAssistantFab } from "../voice/VoiceAssistantFab";
import { useEffect, useState } from "react";

const MIGRATE_KEY = "un8.migrate.offer.v1";

export function WelcomeScreen() {
  const { next, answers, profileAnswers } = useFlow();
  const { session, logout } = useAuth();
  const t = useT();
  const [apiOk, setApiOk] = useState(false);
  const [migrateOffer, setMigrateOffer] = useState(false);
  const [migrateMsg, setMigrateMsg] = useState<string | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(() => shouldShowIntro());

  useEffect(() => {
    void apiHealth(true).then(setApiOk);
  }, []);

  useEffect(() => {
    if (!session || !apiOk || !isServerMode()) return;
    try {
      if (localStorage.getItem(MIGRATE_KEY) === "done") return;
    } catch {
      return;
    }
    const self = matchStore.getState().self;
    if (self || answers.firstName.trim()) {
      setMigrateOffer(true);
    }
  }, [session, apiOk, answers.firstName]);

  const uploadLocalProfile = async () => {
    setMigrateMsg(null);
    const self = matchStore.getState().self;
    const gender = self?.gender || answers.gender;
    if (gender !== "male" && gender !== "female") {
      setMigrateMsg(t("welcome.migrateNeedGender"));
      return;
    }
    const res = await apiJson<{ ok?: boolean; mediaNote?: string; error?: string }>(
      "/api/migrate",
      "POST",
      {
        firstName: self?.firstName || answers.firstName,
        age: self?.age || Number(answers.age) || 25,
        city: self?.city || answers.city,
        gender,
        profession: self?.profession || answers.profession,
        aboutYou: self?.aboutYou || answers.aboutYou,
        lookingFor: self?.lookingFor || answers.lookingFor,
        hobbies: self?.hobbies || answers.hobbies,
        scales: self?.scales || {
          loyalty: answers.scales.loyalty ?? 5,
          honesty: answers.scales.honesty ?? 5,
          passion: answers.scales.passion ?? 5,
          commitment: answers.scales.commitment ?? 5,
          social: answers.scales.social ?? null,
        },
        answers: profileAnswers,
        submitForReview: false,
      }
    );
    try {
      localStorage.setItem(MIGRATE_KEY, "done");
    } catch {
      /* ignore */
    }
    setMigrateOffer(false);
    if (!res.ok) {
      setMigrateMsg(res.error);
      return;
    }
    setMigrateMsg(res.data.mediaNote || t("welcome.migrateDone"));
  };

  const skipMigrate = () => {
    try {
      localStorage.setItem(MIGRATE_KEY, "done");
    } catch {
      /* ignore */
    }
    setMigrateOffer(false);
  };

  const deleteAccount = async () => {
    if (!window.confirm(t("welcome.deleteConfirm"))) {
      return;
    }
    const res = await authStore.deleteAccount();
    if (!res.ok) {
      setDeleteMsg(res.error);
      return;
    }
    setDeleteMsg(t("welcome.accountDeleted"));
    logout();
  };

  if (showIntro) {
    return <IntroStage onDone={() => setShowIntro(false)} />;
  }

  return (
    <>
      <div className="welcome screen-enter">
        <div className="welcome__lang">
          <LanguagePicker compact />
        </div>
        <BrandMark hero />
        <div>
          <p className="welcome__promise">{t("welcome.promise")}</p>
          <p className="welcome__sub" style={{ marginTop: 12 }}>
            {t("welcome.sub")}
          </p>
          {session && !session.email.endsWith("@guest.un8.local") ? (
            <p className="auth-session" style={{ marginTop: 16 }}>
              {t("welcome.signedInAs", { email: session.email })}
              {apiOk ? t("welcome.liveServer") : t("welcome.deviceOnly")}
            </p>
          ) : null}
          {migrateOffer ? (
            <div className="stack" style={{ marginTop: 16, textAlign: "left" }}>
              <p className="welcome__sub">{t("welcome.migrateOffer")}</p>
              <Button onClick={() => void uploadLocalProfile()}>
                {t("welcome.uploadLocalProfile")}
              </Button>
              <Button variant="ghost" onClick={skipMigrate}>
                {t("welcome.keepOnDevice")}
              </Button>
            </div>
          ) : null}
          {migrateMsg ? <p className="welcome__sub">{migrateMsg}</p> : null}
          {deleteMsg ? <p className="welcome__sub">{deleteMsg}</p> : null}
        </div>
      </div>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0, gap: 10 }}>
        <Button onClick={next}>
          {session ? t("welcome.continueOnboarding") : t("welcome.begin")}
        </Button>
        {!session ? (
          <Button variant="ghost" onClick={() => { window.location.hash = "#/auth"; }}>
            {t("welcome.logIn")}
          </Button>
        ) : (
          <Button
            variant="ghost"
            onClick={() => {
              logout();
            }}
          >
            {t("welcome.logOut")}
          </Button>
        )}
        {session && apiOk ? (
          <Button variant="ghost" onClick={() => void deleteAccount()}>
            {t("welcome.deleteMyAccount")}
          </Button>
        ) : null}
      </div>
      <VoiceAssistantFab />
    </>
  );
}
